import { useCallback, useEffect, useMemo, useRef } from "react";

import { GridSortDirection } from "@mui/x-data-grid";

import { UserAnalyticsService } from "@analytics/index";

import industries from "@components/FilterControls/utilities/Industries";

import { CompanyType } from "@interfaces/company.type";
import { SearchType } from "@interfaces/ipsearch.type";

import embeddingSearchSpecificColumns from "@pages/Companies/configs/embeddingSearchSpecificColumns";
import generateCompanyColumns from "@pages/Companies/configs/generateCompanyColumns";
import { addIndustryToFilter, handleRowFocus } from "@pages/Companies/utils";

import { ICompanyTableControls } from "@store/filters/filter.types";
import { useTableStore } from "@store/filters/filters.store";

export interface ICompanyRowData {
  companyId: string;
  country: null | string;
  id: string;
  key: string;
  logo: { alt: string; url: string };
  name: string;
  numberPatents: null | string;
  numEmployees: number | string;
  primaryDomain: null | string;
  rowActions: null | string;
  sector: string[];
  similarityScore: null | number;
}

interface IEmbeddingTableConfig {
  companies: CompanyType[];
  setFocusedId: (id: string) => void;
  smartSearch?: SearchType;
}

const useEmbeddingTableConfig = ({ companies, setFocusedId, smartSearch }: IEmbeddingTableConfig) => {
  const { filters, pagination, selection, setTableFilters, sort } = useTableStore("company");

  const oldFiltersObject = useRef<ICompanyTableControls>();

  useEffect(() => {
    if (oldFiltersObject.current) {
      UserAnalyticsService.trackCompanyFilter(
        {
          filters,
          pagination,
          selection,
          sort,
        },
        oldFiltersObject.current,
        "smart-search",
      );
    }
    oldFiltersObject.current = {
      filters,
      pagination,
      selection,
      sort,
    };
  }, [filters]);

  const handleAddIndustryToFilter = useCallback(
    (newIndustry: string) => {
      addIndustryToFilter({
        filtersObject: {
          filters,
          pagination,
          selection,
          sort,
        },
        handleSetFilters: setTableFilters,
        industries,
        newIndustry,
      });
    },
    [filters, setTableFilters],
  );

  const columns = generateCompanyColumns({
    addIndustryToFilter: handleAddIndustryToFilter,
    filtersObject: {
      filters,
      pagination,
      selection,
      sort,
    },
    preferredIndustries: smartSearch?.preferredIndustries
      ? smartSearch.preferredIndustries.map((industry) => industry.toLowerCase())
      : [],
  });

  const updatedColumns = [...columns.slice(0, 4), ...embeddingSearchSpecificColumns, ...columns.slice(4)];

  const rows: ICompanyRowData[] = useMemo(
    () =>
      (companies || []).map((company) => {
        return {
          companyId: company.id,
          country: company.country || null,
          hasLicensed: company.hasSec,
          hasSponsored: company.hasSponsor,
          id: company.id,
          key: company.id,
          logo: { alt: company.name || "", url: company.logoUrl || "" },
          name: company.name || "",
          numberPatents: company.numberMatchedPatents || "-",
          numEmployees: company.estimatedNumEmployees || "-",
          preferredIndustries: smartSearch?.preferredIndustries
            ? smartSearch.preferredIndustries.map((industry) => industry.toLowerCase())
            : [],
          primaryDomain: company.primaryDomain,
          rowActions: company.id || null,
          sector: company.industries ?? [],
          similarityScore: company.patentSimilarityScore || null,
        };
      }),
    [companies],
  );

  const initialState = {
    pagination: { paginationModel: { page: 0, pageSize: 50 } },
    sorting: {
      sortModel: [{ field: "similarityScore", sort: "desc" as GridSortDirection }],
    },
  };

  return {
    columns: updatedColumns,
    handleRowFocus: handleRowFocus({ companies, setFocusedId }),
    initialState,
    rows,
  };
};

export default useEmbeddingTableConfig;
